import { FileResultDto } from 'business/models/Models';
import ExperimentService from 'business/services/ExperimentService';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { downloadFilefromUrl, getFileURL, showAlertSnackBar } from 'utils/Global';
import { MIME_TYPES } from 'utils/MimeTypes';

const MULTI_TEXT_EXPORT_GAP_MS = 300;

const TEXT_EXPORT_NODE_TYPES = new Set<string>([
  ETreeNodeTypes.Experiment,
  ETreeNodeTypes.Screen,
]);

export type TextExportNode = {
  type?: string;
  name?: string;
  extra?: { id?: number } | null;
};

export function getTextExportTargets(nodes: TextExportNode[] | null | undefined) {
  return (nodes ?? []).flatMap((node) => {
    const id = node?.extra?.id;
    if (!node?.type || !TEXT_EXPORT_NODE_TYPES.has(node.type) || id == null) {
      return [];
    }
    return [{ name: node.name ?? '', id: Number(id) }];
  });
}

export async function ExportToXMLScreenfromExpId(expId: number, expName: string) {
  const xmlFileData: FileResultDto = await new ExperimentService().getXMlScreenFileData(
    expId,
  );
  if (xmlFileData.success === true) {
    const url = getFileURL(xmlFileData.fileData, 'text/xml');
    downloadFilefromUrl(url, expName + ' - Screen.xml');
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'File succesfully downloaded!',
    );
  } else {
    showAlertSnackBar(
      EAlertType.ERROR,
      ESnackBarPosition.TOP,
      'Error: ' + xmlFileData.message,
    );
  }
}

export const exportTextScreen = async (
  expName: string,
  wellIds: number[],
  expId = -1,
  plateIds?: number[] | null,
) => {
  const fileResult = await new ExperimentService().getWellsDataAsTextFile(
    wellIds,
    expId,
    plateIds ?? undefined,
  );
  if (fileResult.success == true) {
    const url = getFileURL(fileResult.fileData, MIME_TYPES.txt);
    downloadFilefromUrl(url, expName + ' - Wells.txt');
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'File succesfully downloaded!',
    );
  } else {
    showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, fileResult.message);
  }
};

export async function exportTextForNodes(
  nodes: TextExportNode[] | null | undefined,
  getPlateIds: (id: number) => number[] | null = () => null,
  gapMs = MULTI_TEXT_EXPORT_GAP_MS,
  exportText: typeof exportTextScreen = exportTextScreen,
): Promise<void> {
  const targets = getTextExportTargets(nodes);
  for (let index = 0; index < targets.length; index += 1) {
    const target = targets[index];
    await exportText(target.name, [], target.id, getPlateIds(target.id));
    if (index < targets.length - 1 && gapMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, gapMs));
    }
  }
}

export function getXmlExportTargets(nodes: TextExportNode[] | null | undefined) {
  return (nodes ?? []).flatMap((node) => {
    const id = node?.extra?.id;
    if (
      !node?.type ||
      (node.type !== ETreeNodeTypes.Experiment && node.type !== ETreeNodeTypes.Screen) ||
      id == null
    ) {
      return [];
    }
    return [{ name: node.name ?? '', id: Number(id), type: node.type }];
  });
}

export async function exportXmlForNodes(
  nodes: TextExportNode[] | null | undefined,
  getPlateIds: (id: number) => number[] | null = () => null,
  gapMs = MULTI_TEXT_EXPORT_GAP_MS,
  exportExperiment: typeof ExportToXMLExperimentfromExpId = ExportToXMLExperimentfromExpId,
  exportScreen: typeof ExportToXMLScreenfromExpId = ExportToXMLScreenfromExpId,
): Promise<void> {
  const targets = getXmlExportTargets(nodes);
  for (let index = 0; index < targets.length; index += 1) {
    const target = targets[index];
    if (target.type === ETreeNodeTypes.Screen) {
      await exportScreen(target.id, target.name);
    } else {
      await exportExperiment(target.id, target.name, getPlateIds(target.id));
    }
    if (index < targets.length - 1 && gapMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, gapMs));
    }
  }
}

export async function ExportToXMLExperimentfromExpId(
  expId: number,
  expName: string,
  plateIds?: number[] | null,
) {
  const xmlFileData: FileResultDto =
    await new ExperimentService().getXMlExperimentFileData(expId, plateIds ?? undefined);
  if (xmlFileData.success === true) {
    const url = getFileURL(xmlFileData.fileData, 'text/xml');
    downloadFilefromUrl(url, expName + '.xml');
    showAlertSnackBar(
      EAlertType.SUCCESS,
      ESnackBarPosition.TOP,
      'File succesfully downloaded!',
    );
  } else {
    showAlertSnackBar(
      EAlertType.ERROR,
      ESnackBarPosition.TOP,
      'Error: ' + xmlFileData.message,
    );
  }
}
